// Flat React Query hook collection wrapping the API client.
// Extend with your solution-specific hooks as features are added.
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { TeamRole, ApiKeyScope, WebhookEvent, IssuerSettings } from '../types';

export type AwardsFilter = { badgeId?: string; search?: string; searchField?: 'name' | 'email'; notEmailedOnly?: boolean; count?: boolean };

// User
export function useUser() {
  return useQuery({
    queryKey: ['user'],
    queryFn: () => api.getUser(),
    retry: 1,
    staleTime: 30_000
  });
}

// Plan usage / limits
export function useUsage() {
  return useQuery({
    queryKey: ['usage'],
    queryFn: () => api.getUsage(),
    retry: 1,
    staleTime: 15_000
  });
}

// Per-award email delivery status (lazy, per awards-list row).
export function useAwardDelivery(awardGuid: string) {
  return useQuery({
    queryKey: ['award-delivery', awardGuid],
    queryFn: () => api.getAwardDelivery(awardGuid),
    enabled: !!awardGuid,
    retry: 1,
    staleTime: 60_000
  });
}

// Aggregate email engagement for the issuer (Analytics page).
export function useAnalytics(range = '30d') {
  return useQuery({
    queryKey: ['analytics', range],
    queryFn: () => api.getAnalytics(range),
    retry: 1,
    staleTime: 60_000
  });
}

export function useEmailStats(range = '30d') {
  return useQuery({
    queryKey: ['email-stats', range],
    queryFn: () => api.getEmailStats(range),
    retry: 1,
    staleTime: 60_000
  });
}

// Billing
export function useCreateCheckout() {
  return useMutation({
    mutationFn: (plan: string) => api.createCheckout(plan),
    onSuccess: ({ url }) => {
      window.location.href = url;
    }
  });
}

export function useCreatePortal() {
  return useMutation({
    mutationFn: () => api.createPortal(),
    onSuccess: ({ url }) => {
      window.location.href = url;
    }
  });
}

// D1 — self-serve test badge; refresh usage/badges so the checklist advances.
export function useSendTestBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.sendTestBadge(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['usage'] });
      void qc.invalidateQueries({ queryKey: ['badges'] });
      void qc.invalidateQueries({ queryKey: ['issuers'] });
    }
  });
}

// D5 — recipient engagement rollup.
export function useEngagement() {
  return useQuery({
    queryKey: ['engagement'],
    queryFn: () => api.getEngagement(),
    staleTime: 60_000
  });
}

// API keys
export function useApiKeys() {
  return useQuery({
    queryKey: ['apiKeys'],
    queryFn: () => api.listApiKeys(),
    staleTime: 30_000
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, scopes }: { name: string; scopes?: ApiKeyScope[] }) => api.createApiKey(name, scopes),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['apiKeys'] }); }
  });
}

export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.deleteApiKey(key),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['apiKeys'] }); }
  });
}

// Webhooks
export function useWebhooks() {
  return useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.listWebhooks(),
    staleTime: 30_000
  });
}

export function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ url, events }: { url: string; events: WebhookEvent[] }) => api.createWebhook(url, events),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['webhooks'] }); }
  });
}

export function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteWebhook(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['webhooks'] }); }
  });
}

// Team members
export function useMembers() {
  return useQuery({
    queryKey: ['members'],
    queryFn: () => api.listMembers(),
    staleTime: 30_000
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: TeamRole }) => api.inviteMember(email, role),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['members'] }); }
  });
}

export function useUpdateMemberRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: TeamRole }) => api.updateMemberRole(memberId, role),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['members'] }); }
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => api.removeMember(memberId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['members'] }); }
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => api.acceptInvite(inviteId),
    onSuccess: () => { void qc.invalidateQueries(); }
  });
}

// Support tickets
export function useSupportTickets() {
  return useQuery({
    queryKey: ['support-tickets'],
    queryFn: () => api.listSupportTickets(),
    staleTime: 30_000
  });
}

export function useSupportTicket(ticketId: string) {
  return useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: () => api.getSupportTicket(ticketId),
    enabled: !!ticketId,
    staleTime: 15_000
  });
}

export function useCreateSupportTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, message, category }: { subject: string; message: string; category: string }) =>
      api.createSupportTicket(subject, message, category),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['support-tickets'] }); }
  });
}

export function useReplySupportTicket(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: string) => api.replySupportTicket(ticketId, message),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      void qc.invalidateQueries({ queryKey: ['support-tickets'] });
    }
  });
}

// Issuers
export function useIssuers() {
  return useQuery({
    queryKey: ['issuers'],
    queryFn: () => api.listIssuers(),
    staleTime: 30_000
  });
}

export function useCreateIssuer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; url: string; email: string; logo?: string; linkedinOrganizationId?: string }) => api.createIssuer(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['issuers'] }); }
  });
}

export function useEditIssuer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ issuerId, data }: { issuerId: string; data: { name: string; url: string; email: string; logo?: string; linkedinOrganizationId?: string } }) =>
      api.editIssuer(issuerId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['issuers'] }); }
  });
}

export function useVerifyIssuer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ issuerId, code }: { issuerId: string; code: string }) => api.verifyIssuer(issuerId, code),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['issuers'] }); }
  });
}

export function useDeleteIssuer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (issuerId: string) => api.deleteIssuer(issuerId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['issuers'] }); }
  });
}

export function useIssuerSettings(issuerId: string | null) {
  return useQuery({
    queryKey: ['issuerSettings', issuerId],
    queryFn: () => api.getIssuerSettings(issuerId as string),
    enabled: !!issuerId,
    staleTime: 30_000
  });
}

export function useUpdateIssuerSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ issuerId, patch }: { issuerId: string; patch: IssuerSettings }) => api.updateIssuerSettings(issuerId, patch),
    onSuccess: (_data, vars) => { void qc.invalidateQueries({ queryKey: ['issuerSettings', vars.issuerId] }); }
  });
}

// Badges
export function useBadges() {
  return useQuery({
    queryKey: ['badges'],
    queryFn: () => api.listBadges(),
    staleTime: 30_000
  });
}

// Account badge templates (reusable saved designs).
export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api.listTemplates(),
    staleTime: 30_000
  });
}
export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; designJson: string; image?: string }) => api.createTemplate(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); }
  });
}
export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteTemplate(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); }
  });
}

// Badge drafts (server-side, cross-device).
export function useDrafts() {
  return useQuery({
    queryKey: ['drafts'],
    queryFn: () => api.listDrafts(),
    staleTime: 15_000
  });
}
export function useCreateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof api.createDraft>[0]) => api.createDraft(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['drafts'] }); }
  });
}
export function useUpdateDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof api.updateDraft>[1] }) => api.updateDraft(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['drafts'] }); }
  });
}
export function useDeleteDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteDraft(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['drafts'] }); }
  });
}

// Single badge incl. designJson — used by the editor when entering edit mode.
export function useBadge(badgeId: string | undefined) {
  return useQuery({
    queryKey: ['badge', badgeId],
    queryFn: () => api.getBadge(badgeId!),
    enabled: !!badgeId,
    staleTime: 0
  });
}

export function useCreateBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description: string; criteria: string; image: string; issuerId: string; shareText?: string; designJson?: string; designSvg?: string }) => api.createBadge(data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['badges'] }); }
  });
}

export function useUpdateBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ badgeId, data }: { badgeId: string; data: { name?: string; description?: string; criteria?: string; image?: string; designJson?: string; designSvg?: string } }) => api.updateBadge(badgeId, data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['badges'] });
      qc.invalidateQueries({ queryKey: ['badge', vars.badgeId] });
    }
  });
}

export function useUpdateBadgeShareText() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ badgeId, text }: { badgeId: string; text: string }) => api.updateBadgeShareText(badgeId, text),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['badges'] }); }
  });
}

export function useDeleteBadge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (badgeId: string) => api.deleteBadge(badgeId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['badges'] }); }
  });
}

// Certificate design (F1) — per badge
export function useBadgeCertificate(badgeId: string | null) {
  return useQuery({
    queryKey: ['badgeCertificate', badgeId],
    queryFn: () => api.getBadgeCertificate(badgeId as string),
    enabled: !!badgeId,
    staleTime: 30_000
  });
}

export function useSaveBadgeCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ badgeId, certificateSvg }: { badgeId: string; certificateSvg: string }) => api.saveBadgeCertificate(badgeId, certificateSvg),
    onSuccess: (_d, vars) => { void qc.invalidateQueries({ queryKey: ['badgeCertificate', vars.badgeId] }); }
  });
}

export function useRevokeAward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ awardId, reason, notify }: { awardId: string; reason?: string; notify?: boolean }) => api.revokeAward(awardId, reason, notify),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['awards'] }); }
  });
}

export function useEditAward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ awardId, fields }: { awardId: string; fields: { name?: string; email?: string; issuedOn?: string; expires?: string | null } }) =>
      api.editAward(awardId, fields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['awards'] });
      qc.invalidateQueries({ queryKey: ['awards-count'] });
    }
  });
}

export function useDeleteAward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (awardId: string) => api.deleteAward(awardId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['awards'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    }
  });
}

// Awards
/**
 * Single-page awards query — kept as a thin compat wrapper so legacy callers
 * (e.g., the badge edit award-count gate that only needs "any awards exist?")
 * don't need to change. Internally this is just the first page of
 * useInfiniteAwards' data.
 */
export function useAwards(filter?: AwardsFilter, lastEvaluatedKey?: string, enabled = true) {
  return useQuery({
    queryKey: ['awards', filter, lastEvaluatedKey],
    queryFn: () => api.listAwards(filter, lastEvaluatedKey),
    staleTime: 30_000,
    enabled
  });
}

/**
 * Cursor-paginated awards. Returns flattened items + helpers for "Load more".
 * The backend page size is 50; pages are fetched on demand via fetchNextPage().
 */
export function useInfiniteAwards(filter?: AwardsFilter, enabled = true) {
  return useInfiniteQuery({
    queryKey: ['awards-infinite', filter],
    queryFn: ({ pageParam }) => api.listAwards(filter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.lastEvaluatedKey,
    staleTime: 30_000,
    enabled,
  });
}

export function useCreateAward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { badgeId: string; recipient: { name: string; email: string }; issuedOn: number; expires?: number }) => api.createAward(data),
    onSuccess: () => {
      // Invalidate every queryKey that depends on the awards table:
      //   ['awards']           — useAwards (paginated, used by BadgesPage)
      //   ['awards-infinite']  — useInfiniteAwards (the AwardsPage list itself)
      //   ['usage']            — usage.awardsThisYear + the limit-warning banner
      qc.invalidateQueries({ queryKey: ['awards'] });
      qc.invalidateQueries({ queryKey: ['awards-infinite'] });
      qc.invalidateQueries({ queryKey: ['usage'] });
    }
  });
}

export function useSendAward() {
  return useMutation({
    mutationFn: ({ awardId, email }: { awardId: string; email: string }) => api.sendAward(awardId, email)
  });
}

export function useShareAward() {
  return useMutation({
    mutationFn: ({ awardId, recipients, subject, message }: { awardId: string; recipients: string; subject: string; message: string }) =>
      api.shareAward(awardId, recipients, subject, message)
  });
}
